// filepath: /client/src/components/FlagManagement.js
import React, { useState, useEffect } from 'react';
import { getAllCustomerFlags, unflagCustomer, deleteCustomerFlag, updateCustomerFlag, createCustomerFlag } from '../api';

/**
 * Admin interface for viewing and managing customer flags
 * Allows managers to view all flags, unflag customers, or permanently delete flags
 */
const FlagManagement = () => {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editReason, setEditReason] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newFlag, setNewFlag] = useState({
    guest_name: '',
    guest_email: '',
    guest_contact: '',
    ip_address: '',
    reason: ''
  });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Fetch flags on component mount and when showInactive changes
  useEffect(() => {
    loadFlags();
  }, [showInactive]);

  const loadFlags = async () => {
    setLoading(true);
    try {
      const response = await getAllCustomerFlags(showInactive);
      setFlags(response.flags || []);
    } catch (error) {
      console.error('Error loading flags:', error);
      showToast('Failed to load flags', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const handleUnflag = async (flagId) => {
    if (!window.confirm('Are you sure you want to unflag this customer? They will no longer trigger warnings.')) {
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await unflagCustomer(flagId, user.username || 'manager');
      showToast('Customer unflagged successfully');
      loadFlags();
    } catch (error) {
      console.error('Error unflagging customer:', error);
      showToast('Failed to unflag customer', 'error');
    }
  };

  const handleDelete = async (flagId) => {
    if (!window.confirm('Are you sure you want to PERMANENTLY delete this flag? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteCustomerFlag(flagId);
      showToast('Flag deleted permanently');
      loadFlags();
    } catch (error) {
      console.error('Error deleting flag:', error);
      showToast('Failed to delete flag', 'error');
    }
  };

  const handleEditReason = (flag) => {
    setSelectedFlag(flag);
    setEditReason(flag.reason);
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editReason.trim()) {
      showToast('Reason cannot be empty', 'error');
      return;
    }

    try {
      await updateCustomerFlag(selectedFlag.id, editReason.trim());
      showToast('Flag reason updated successfully');
      setShowEditModal(false);
      loadFlags();
    } catch (error) {
      console.error('Error updating flag:', error);
      showToast('Failed to update flag', 'error');
    }
  };

  const handleAddNewFlag = () => {
    setNewFlag({
      guest_name: '',
      guest_email: '',
      guest_contact: '',
      ip_address: '',
      reason: ''
    });
    setShowAddModal(true);
  };

  const handleSaveNewFlag = async () => {
    // Validate required fields
    if (!newFlag.guest_name.trim()) {
      showToast('Guest name is required', 'error');
      return;
    }
    if (!newFlag.reason.trim()) {
      showToast('Reason is required', 'error');
      return;
    }

    // At least one contact method is required
    if (!newFlag.guest_email.trim() && !newFlag.guest_contact.trim() && !newFlag.ip_address.trim()) {
      showToast('At least one contact method (email, phone, or IP) is required', 'error');
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await createCustomerFlag({
        reservation_id: null, // No booking reference
        guest_name: newFlag.guest_name.trim(),
        guest_email: newFlag.guest_email.trim() || null,
        guest_contact: newFlag.guest_contact.trim() || null,
        ip_address: newFlag.ip_address.trim() || null,
        reason: newFlag.reason.trim(),
        flagged_by: user.username || 'admin'
      });
      showToast('Customer flagged successfully');
      setShowAddModal(false);
      loadFlags();
    } catch (error) {
      console.error('Error creating flag:', error);
      showToast(error.response?.data?.error || 'Failed to create flag', 'error');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{
      padding: '24px',
      fontFamily: 'Segoe UI, Arial, sans-serif',
      backgroundColor: '#f5f7fa',
      minHeight: '100vh'
    }}>
      {/* Toast Notification */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: 24,
          right: 24,
          padding: '16px 24px',
          borderRadius: '8px',
          backgroundColor: toast.type === 'error' ? '#f44336' : '#4caf50',
          color: 'white',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 10000,
          fontWeight: '600',
          animation: 'slideIn 0.3s ease'
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <h1 style={{
              margin: '0 0 8px 0',
              color: '#2a3b5c',
              fontSize: '28px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span>🚩</span> Flagged Customers
            </h1>
            <p style={{
              margin: 0,
              color: '#666',
              fontSize: '14px'
            }}>
              Manage customer flags and monitor unruly guests
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              padding: '8px 16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              fontSize: '14px'
            }}>
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Show Inactive Flags
            </label>
            <button
              onClick={handleAddNewFlag}
              style={{
                padding: '10px 20px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#d32f2f',
                color: 'white',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                boxShadow: '0 2px 6px rgba(211, 47, 47, 0.3)'
              }}
            >
              ➕ Add New Flag
            </button>
            <button
              onClick={loadFlags}
              style={{
                padding: '10px 20px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: '#4f8cff',
                color: 'white',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Flags List */}
      {loading ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: 'white',
          borderRadius: '12px',
          color: '#666'
        }}>
          Loading flags...
        </div>
      ) : flags.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px',
          backgroundColor: 'white',
          borderRadius: '12px',
          color: '#666'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
          <h3 style={{ color: '#2a3b5c', marginBottom: '8px' }}>No Flags Found</h3>
          <p>There are no {showInactive ? '' : 'active '}flagged customers at this time.</p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gap: '16px'
        }}>
          {flags.map((flag) => (
            <div
              key={flag.id}
              style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: flag.is_active ? '4px solid #d32f2f' : '4px solid #9e9e9e'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'start',
                gap: '20px',
                flexWrap: 'wrap'
              }}>
                {/* Flag Info */}
                <div style={{ flex: 1, minWidth: '300px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '12px'
                  }}>
                    <span style={{ fontSize: '24px' }}>
                      {flag.is_active ? '🚩' : '⚪'}
                    </span>
                    <div>
                      <h3 style={{
                        margin: '0 0 4px 0',
                        color: '#2a3b5c',
                        fontSize: '18px'
                      }}>
                        {flag.guest_name}
                      </h3>
                      <div style={{
                        fontSize: '12px',
                        color: '#999',
                        fontWeight: '600'
                      }}>
                        {flag.is_active ? (
                          <span style={{ color: '#d32f2f' }}>● ACTIVE</span>
                        ) : (
                          <span style={{ color: '#9e9e9e' }}>● INACTIVE</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Customer Details */}
                  <div style={{
                    backgroundColor: '#f8f9fa',
                    padding: '12px',
                    borderRadius: '6px',
                    marginBottom: '12px',
                    fontSize: '14px'
                  }}>
                    <div style={{ marginBottom: '6px' }}>
                      <strong>Booking:</strong> {flag.booking_no || 'N/A'}
                    </div>
                    {flag.guest_email && (
                      <div style={{ marginBottom: '6px' }}>
                        <strong>Email:</strong> {flag.guest_email}
                      </div>
                    )}
                    {flag.guest_contact && (
                      <div style={{ marginBottom: '6px' }}>
                        <strong>Contact:</strong> {flag.guest_contact}
                      </div>
                    )}
                    {flag.ip_address && (
                      <div>
                        <strong>IP Address:</strong> {flag.ip_address}
                      </div>
                    )}
                  </div>

                  {/* Reason */}
                  <div style={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    padding: '12px',
                    borderRadius: '6px',
                    marginBottom: '12px'
                  }}>
                    <strong style={{ fontSize: '13px', color: '#856404' }}>Reason:</strong>
                    <p style={{
                      margin: '6px 0 0 0',
                      color: '#856404',
                      fontSize: '14px',
                      lineHeight: '1.5'
                    }}>
                      {flag.reason}
                    </p>
                  </div>

                  {/* Metadata */}
                  <div style={{
                    fontSize: '12px',
                    color: '#999',
                    display: 'flex',
                    gap: '16px',
                    flexWrap: 'wrap'
                  }}>
                    <div>
                      <strong>Flagged by:</strong> {flag.flagged_by || 'Unknown'}
                    </div>
                    <div>
                      <strong>Date:</strong> {formatDate(flag.created_at)}
                    </div>
                    {!flag.is_active && flag.updated_at !== flag.created_at && (
                      <div>
                        <strong>Unflagged:</strong> {formatDate(flag.updated_at)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                {flag.is_active && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    minWidth: '140px'
                  }}>
                    <button
                      onClick={() => handleEditReason(flag)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: '#4f8cff',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      ✏️ Edit Reason
                    </button>
                    <button
                      onClick={() => handleUnflag(flag.id)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: '#4caf50',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      ✅ Unflag
                    </button>
                    <button
                      onClick={() => handleDelete(flag.id)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: '#f44336',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Reason Modal */}
      {showEditModal && (
        <div 
          onClick={() => setShowEditModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
          >
            <h3 style={{ marginTop: 0, color: '#2a3b5c' }}>Edit Flag Reason</h3>
            <textarea
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              rows={5}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                boxSizing: 'border-box',
                marginBottom: '16px'
              }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  backgroundColor: 'white',
                  color: '#666',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#4f8cff',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Flag Modal */}
      {showAddModal && (
        <div 
          onClick={() => setShowAddModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '600px',
              width: '100%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '24px',
              paddingBottom: '16px',
              borderBottom: '2px solid #fee'
            }}>
              <span style={{ fontSize: '32px' }}>🚩</span>
              <h3 style={{ margin: 0, color: '#d32f2f', fontSize: '24px' }}>
                Add New Flag
              </h3>
            </div>

            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '20px',
              color: '#856404',
              fontSize: '14px'
            }}>
              <strong>Note:</strong> Use this to manually flag a customer based on phone calls or other non-booking interactions. At least one contact method is required for future matching.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Guest Name */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontWeight: 'bold',
                  color: '#2a3b5c',
                  fontSize: '14px'
                }}>
                  Guest Name <span style={{ color: '#d32f2f' }}>*</span>
                </label>
                <input
                  type="text"
                  value={newFlag.guest_name}
                  onChange={(e) => setNewFlag({...newFlag, guest_name: e.target.value})}
                  placeholder="Enter guest full name"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Email */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontWeight: 'bold',
                  color: '#2a3b5c',
                  fontSize: '14px'
                }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={newFlag.guest_email}
                  onChange={(e) => setNewFlag({...newFlag, guest_email: e.target.value})}
                  placeholder="guest@example.com"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Contact Number */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontWeight: 'bold',
                  color: '#2a3b5c',
                  fontSize: '14px'
                }}>
                  Contact Number
                </label>
                <input
                  type="text"
                  value={newFlag.guest_contact}
                  onChange={(e) => setNewFlag({...newFlag, guest_contact: e.target.value})}
                  placeholder="+63 912 345 6789"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* IP Address */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontWeight: 'bold',
                  color: '#2a3b5c',
                  fontSize: '14px'
                }}>
                  IP Address
                </label>
                <input
                  type="text"
                  value={newFlag.ip_address}
                  onChange={(e) => setNewFlag({...newFlag, ip_address: e.target.value})}
                  placeholder="192.168.1.1"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Reason */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontWeight: 'bold',
                  color: '#2a3b5c',
                  fontSize: '14px'
                }}>
                  Reason for Flagging <span style={{ color: '#d32f2f' }}>*</span>
                </label>
                <textarea
                  value={newFlag.reason}
                  onChange={(e) => setNewFlag({...newFlag, reason: e.target.value})}
                  placeholder="Enter detailed reason for flagging (e.g., threatening behavior during phone call, known troublemaker)..."
                  rows={5}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  padding: '12px 24px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  backgroundColor: 'white',
                  color: '#666',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '15px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNewFlag}
                style={{
                  padding: '12px 28px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#d32f2f',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '15px',
                  boxShadow: '0 2px 8px rgba(211, 47, 47, 0.3)'
                }}
              >
                🚩 Flag Customer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlagManagement;
